import React from 'react';
import { Copy, Mail } from 'lucide-react';
import { toast } from 'sonner';

const enc = encodeURIComponent;

// Reusable "share this product with a colleague" bar — Copy summary + Email,
// mirroring the Ortho comparison share pattern. Blue is the universal data accent.
const ProductShareBar = ({
  title,
  pageUrl,
  summary,
  subject,
  testidPrefix,
  accent = '#3b82f6',
  note = 'Copies a procurement-ready summary with key specs and sources — no data leaves your device.',
}) => {
  const tint = (a) => {
    const m = accent.replace('#', '');
    const r = parseInt(m.substring(0, 2), 16), g = parseInt(m.substring(2, 4), 16), b = parseInt(m.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
  };
  const btnStyle = { borderColor: tint(0.4), backgroundColor: tint(0.1), color: accent };
  const fullText = `${summary}\n\n${pageUrl}`;
  const mailto = `mailto:?subject=${enc(subject)}&body=${enc(fullText)}`;

  const onCopy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(fullText);
      } else {
        const ta = document.createElement('textarea');
        ta.value = fullText;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success(`${title} summary copied to clipboard`, {
        description: 'Paste it into an email, chat or document to share with a colleague.',
      });
      if (window.gtag) window.gtag('event', 'share_click', { method: 'copy', content_type: `${testidPrefix}_summary` });
    } catch (e) {
      toast.error('Could not copy automatically — please try again.');
    }
  };

  const onEmail = () => {
    if (window.gtag) window.gtag('event', 'share_click', { method: 'email', content_type: `${testidPrefix}_summary` });
  };

  return (
    <div data-testid={`${testidPrefix}-share-bar`} className="flex flex-wrap items-center gap-3">
      <button
        type="button"
        onClick={onCopy}
        data-testid={`${testidPrefix}-copy-summary`}
        className="group inline-flex items-center gap-2.5 rounded-full border px-5 py-2.5 text-sm font-bold transition-all duration-300 hover:brightness-110"
        style={btnStyle}
      >
        <Copy className="h-4 w-4 transition-transform group-hover:-translate-y-0.5" strokeWidth={2.2} />
        Copy summary
      </button>
      <a
        href={mailto}
        onClick={onEmail}
        data-testid={`${testidPrefix}-email-summary`}
        className="group inline-flex items-center gap-2.5 rounded-full border px-5 py-2.5 text-sm font-bold transition-all duration-300 hover:brightness-110"
        style={btnStyle}
      >
        <Mail className="h-4 w-4 transition-transform group-hover:-translate-y-0.5" strokeWidth={2.2} />
        Email to a colleague
      </a>
      <span className="basis-full text-xs text-slate-500 sm:basis-auto">{note}</span>
    </div>
  );
};

export default ProductShareBar;
